import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGame } from '../context/GameContext';
import VideoTutorialModal from './VideoTutorialModal';
import mainLogo from '../assets/images/mainlogo.png';
import '../styles/draggable-financial.css';

const MobileTopBarClean = ({ stats = {} }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { gameState } = useGame();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const netWorthRef = useRef(null);
  const dragGhostRef = useRef(null);

  // Register non-passive touch event listeners
  React.useEffect(() => {
    const netWorthElement = netWorthRef.current;
    if (!netWorthElement) return;

    const handleTouchMoveNonPassive = (e) => {
      if (isDragging) {
        e.preventDefault();
        
        // Update drag position for visual feedback
        const touch = e.touches[0];
        if (touch) {
          setDragPosition({
            x: touch.clientX,
            y: touch.clientY
          });
        }
      }
    };

    // Add non-passive touch move listener
    netWorthElement.addEventListener('touchmove', handleTouchMoveNonPassive, { passive: false });

    return () => {
      netWorthElement.removeEventListener('touchmove', handleTouchMoveNonPassive);
    };
  }, [isDragging]);

  // Generate detailed financial explanation data
  const generateFinancialData = () => {
    const netWorth = stats?.netWorth || 0;
    const cash = gameState?.cash || 0;
    const savingsBalance = gameState?.savingsBalance || 0;
    const totalInvested = gameState?.totalInvested || 0;
    const loan = gameState?.loan || 0;
    const portfolio = gameState?.portfolio || [];
    
    // Calculate portfolio details
    const portfolioBreakdown = portfolio.map(position => ({
      symbol: position.symbol,
      quantity: position.quantity,
      avgCost: position.avgCost,
      currentValue: position.quantity * (position.currentPrice || position.avgCost),
      unrealizedPnL: (position.quantity * (position.currentPrice || position.avgCost)) - (position.quantity * position.avgCost)
    }));

    return {
      type: 'financial_data',
      metric: 'net_worth',
      value: netWorth,
      displayValue: `$${Math.round(netWorth).toLocaleString()}`,
      breakdown: {
        cash,
        savingsBalance,
        totalInvested,
        loan,
        calculation: `$${cash} (cash) + $${savingsBalance} (savings) + $${totalInvested} (investments) - $${loan} (loan) = $${netWorth}`,
        portfolioBreakdown
      },
      userName: user?.username || 'Player'
    };
  };

  // Handle drag start (touch and mouse)
  const handleDragStart = (e) => {
    console.log('🎯 NET WORTH DRAG START');
    setIsDragging(true);
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    dragStartPos.current = { x: clientX, y: clientY };
    setDragPosition({ x: clientX, y: clientY });
    
    // For desktop drag
    if (e.dataTransfer) {
      const financialData = generateFinancialData();
      e.dataTransfer.setData('application/json', JSON.stringify(financialData));
      e.dataTransfer.effectAllowed = 'copy';
    }
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    console.log('🎯 NET WORTH DRAG END');
    
    if (!isDragging) return;
    
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    
    // Check if we moved significantly (minimum drag distance)
    const dragDistance = Math.sqrt(
      Math.pow(clientX - dragStartPos.current.x, 2) + 
      Math.pow(clientY - dragStartPos.current.y, 2)
    );
    
    if (dragDistance > 30) {
      // Find element at drop position
      const elementAtPoint = document.elementFromPoint(clientX, clientY);
      
      // Check if dropped on AI assistant or "Ask Me" button
      if (elementAtPoint) {
        const aiAssistant = elementAtPoint.closest('[data-ai-assistant]') || 
                           elementAtPoint.closest('.floating-ai-assistant') ||
                           document.querySelector('.floating-ai-assistant');
        
        if (aiAssistant) {
          console.log('🎯 DROPPED ON AI ASSISTANT - Dispatching financial data event');
          
          // Add success feedback
          if (netWorthRef.current) {
            netWorthRef.current.classList.add('drop-success');
            setTimeout(() => {
              if (netWorthRef.current) {
                netWorthRef.current.classList.remove('drop-success');
              }
            }, 600);
          }
          
          // Dispatch custom event for AI assistant
          const financialData = generateFinancialData();
          const event = new CustomEvent('financialDataDropped', {
            detail: financialData
          });
          window.dispatchEvent(event);
        }
      }
    }
    
    setIsDragging(false);
    setDragPosition({ x: 0, y: 0 });
  };

  // Prevent default drag behavior on mobile
  const handleTouchMove = (e) => {
    if (isDragging) {
      // Don't call preventDefault here due to passive listeners
      // Instead, we'll handle this through CSS touch-action: none
    }
  };
  
  // Spacing adjustments
  const TOP_BAR_HEIGHT = 70;
  const FINANCIAL_BANNER_HEIGHT = 80;
  const GAP_BETWEEN = 5;

  return (
    <div>
      {/* TOP BAR WITH LOGO, NET WORTH, FULL FINANCE, AND USER */}
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: `${TOP_BAR_HEIGHT}px`, 
        background: '#000000',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        borderBottom: '3px solid #FFFFFF',
        boxShadow: '0 4px 0 #333333'
      }}>
        
        {/* LEFT SIDE - LOGO */}
        <div
          onClick={() => {
            // If we're already on dashboard, scroll to top
            if (window.location.pathname === '/dashboard') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/dashboard');
            }
          }}
          style={{
            background: '#FFFFFF',
            border: '3px solid #000000',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '3px 3px 0px #333333'
          }}
        >
          <img
            src={mainLogo}
            alt="Home"
            style={{
              width: '42px',
              height: '42px',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* CENTER LEFT - NET WORTH (DRAGGABLE) */}
        <div 
          ref={netWorthRef}
          draggable={true}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          className={`net-worth-draggable ${isDragging ? 'dragging' : ''}`}
          style={{
            background: isDragging ? '#f0f0f0' : '#FFFFFF',
            border: '3px solid #000000',
            padding: '6px 12px',
            boxShadow: isDragging ? '5px 5px 0px #333333' : '3px 3px 0px #333333',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            transform: isDragging ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            touchAction: 'none'
          }}>
          
          {/* Drag Instructions Tooltip */}
          <div className="drag-instructions">
            Drag to AI for explanation
          </div>
          <div style={{
            fontSize: '6px',
            fontWeight: '700',
            color: '#666666',
            textTransform: 'uppercase',
            marginBottom: '1px',
            fontFamily: "'Arial', sans-serif",
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
          }}>
            NET WORTH
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '900',
            color: '#000000',
            fontFamily: "'Arial Black', sans-serif",
            lineHeight: 1
          }}>
            <span style={{ color: '#D72638' }}>$</span>
            {(() => {
              const netWorth = stats?.netWorth || 1011;
              return Math.round(netWorth).toLocaleString();
            })()}
          </div>
        </div>

        {/* RIGHT SIDE GROUP - FULL FINANCE + USER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          
          {/* FULL FINANCE BUTTON */}
          <button
            onClick={() => navigate('/mobile/financial')}
            style={{
              background: '#000000',
              border: '3px solid #FFFFFF',
              borderRadius: '0',
              padding: '6px 10px',
              boxShadow: '3px 3px 0px #333333',
              fontSize: '10px',
              cursor: 'pointer',
              height: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.1s ease',
              color: '#FFFFFF',
              fontWeight: '900',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontFamily: "'Arial Black', 'Impact', 'Franklin Gothic Medium', 'Arial Narrow', sans-serif",
              transform: 'skew(-3deg)',
              gap: '4px'
            }}
          >
            FULL<br/>FINANCE
          </button>

          {/* USER BUTTON */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              background: '#FFFFFF',
              border: '3px solid #000000',
              borderRadius: '0',
              width: '60px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: '900',
              color: '#000000',
              cursor: 'pointer',
              boxShadow: '3px 3px 0px #333333',
              position: 'relative',
              fontFamily: "'Arial Black', sans-serif"
            }}
          >
            {(user?.username || 'U')[0].toUpperCase()}
            
            {/* Dropdown Arrow */}
            <span style={{
              position: 'absolute',
              bottom: '2px',
              right: '2px',
              fontSize: '8px',
              fontWeight: '900',
              color: '#000000',
              background: '#FFFFFF',
              width: '12px',
              height: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #000000',
              transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </button>

          {/* DROPDOWN MENU */}
          {dropdownOpen && (
            <>
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1001,
                  background: 'rgba(0,0,0,0.3)'
                }}
                onClick={() => setDropdownOpen(false)}
              />
              <div style={{
                position: 'absolute',
                top: '50px',
                right: 0,
                background: '#FFFFFF',
                border: '3px solid #000000',
                boxShadow: '4px 4px 0px #333333',
                minWidth: '150px',
                zIndex: 1002
              }}>
                <div style={{ 
                  padding: '12px', 
                  borderBottom: '2px solid #000000', 
                  fontSize: '14px', 
                  fontWeight: '900',
                  fontFamily: "'Arial Black', sans-serif",
                  background: '#000000',
                  color: '#FFFFFF'
                }}>
                  {user?.username || 'Player'}
                </div>
                
                {/* Go Home */}
                <button
                  onClick={() => {
                    navigate('/dashboard');
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#f59e0b',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Go Home
                </button>
                
                {/* Groups */}
                <button
                  onClick={() => {
                    navigate('/groups');
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#10b981',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Groups
                </button>
                
                {/* Guide Video */}
                <button
                  onClick={() => {
                    setShowVideoModal(true);
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#8B5CF6',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Guide Video
                </button>

                {/* Trade */}
                <button
                  onClick={() => {
                    navigate('/stocks');
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#3b82f6',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Trade
                </button>
                
                {/* Full Finance */}
                <button
                  onClick={() => {
                    navigate('/mobile/financial');
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#ef4444',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Full Finance
                </button>
                
                {/* Settings */}
                <button
                  onClick={() => {
                    navigate('/settings');
                    setDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#6b7280',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center',
                    marginBottom: '4px'
                  }}
                >
                  Settings
                </button>

                {/* Logout */}
                <button
                  onClick={() => { logout(); navigate('/login'); }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#D72638',
                    border: '2px solid #000000',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '900',
                    cursor: 'pointer',
                    fontFamily: "'Arial Black', sans-serif",
                    textAlign: 'center'
                  }}
                >
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
        
        </div> {/* End RIGHT SIDE GROUP */}
        
      </div>

      {/* DRAG GHOST ELEMENT */}
      {isDragging && (
        <div
          ref={dragGhostRef}
          style={{
            position: 'fixed',
            left: dragPosition.x - 35,
            top: dragPosition.y - 25,
            background: '#808080',
            border: '3px solid #000000',
            padding: '6px 12px',
            boxShadow: '6px 6px 0px #333333',
            textAlign: 'center',
            zIndex: 10000,
            pointerEvents: 'none',
            transform: 'scale(1.1) rotate(3deg)',
            opacity: 0.9,
            borderRadius: '0'
          }}
        >
          <div style={{
            fontSize: '6px',
            fontWeight: '700',
            color: '#cccccc',
            textTransform: 'uppercase',
            marginBottom: '1px',
            fontFamily: "'Arial', sans-serif"
          }}>
            NET WORTH
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '900',
            color: '#ffffff',
            fontFamily: "'Arial Black', sans-serif"
          }}>
            <span style={{ color: '#ffff00' }}>$</span>
            {(() => {
              const netWorth = stats?.netWorth || 1011;
              return Math.round(netWorth).toLocaleString();
            })()}
          </div>
        </div>
      )}

      {/* CONTENT SPACER - ONLY FOR TOP BAR */}
      <div style={{ 
        height: `${TOP_BAR_HEIGHT}px`
      }} />

      {/* Video Tutorial Modal */}
      <VideoTutorialModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
      />

    </div>
  );
};

export default MobileTopBarClean;